import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import startCase from 'lodash/startCase';
import { Cell, HeaderCell, HeaderRow, Row, Table, TableBody, TableHead, TooltipIcon } from 'components';
import { theme } from 'design';
import { BankReconciliationReportType, ILedgerEntry, ILedgerEntryByDate } from 'types';
import { centsToDollarsDisplay } from 'utils';

const DAILY_REPORTS_HEADERS: IBankReconciliationTableHeader[] = [{ label: 'date' }, { label: 'amount', align: 'right' }];
const DETAIL_CREDITS_REPORTS_HEADERS: IBankReconciliationTableHeader[] = [{ label: 'date' }, { label: 'type' }, { label: 'description' }, { label: 'amount', align: 'right' }];
const DETAIL_DEBITS_REPORTS_HEADERS: IBankReconciliationTableHeader[] = [{ label: 'date' }, { label: 'type' }, { label: 'description' }, { label: 'employeeName' }, { label: 'transactionId' }, { label: 'amount', align: 'right' }];

interface IBankReconciliationTableHeader {
  label: string;
  align?: 'center' | 'left' | 'right';
}

interface IBankReconciliationTableProps {
  isLoading?: boolean;
  tableData: ILedgerEntryByDate[] | ILedgerEntry[];
  reportType: BankReconciliationReportType;
  dataCy?: string;
}

const DailyReportRow = (data: ILedgerEntryByDate, index: number, dataCy: string, isLoading: boolean) => (
  <Row key={`${dataCy}-row-${index}`} data-cy={`${dataCy}-row`}>
    <Cell data-cy="date">{isLoading ? <Skeleton width={77} /> : data.date}</Cell>
    <Cell align="right" fontWeight="semiBold" data-cy="amount">
      {isLoading ? <Skeleton width={77} /> : centsToDollarsDisplay(data.amount)}
    </Cell>
  </Row>
);

const DetailDebitReportRow = (data: ILedgerEntry, index: number, dataCy: string, isLoading: boolean) => (
  <Row key={`${dataCy}-row-${index}`} data-cy={`${dataCy}-row`}>
    <Cell minWidth={77} data-cy="date">
      {isLoading ? <Skeleton width={77} /> : dayjs(data.createdAt).format('MM-DD-YYYY')}
    </Cell>
    <Cell minWidth={77} data-cy="type">
      {isLoading ? <Skeleton width={77} /> : startCase(data.entryType)}
    </Cell>
    <Cell minWidth={200} maxWidth={200} wrapText data-cy="description">
      {isLoading ? <Skeleton width={200} /> : data.description || '--'}
    </Cell>
    <Cell minWidth={200} maxWidth={200} wrapText data-cy="employeeName">
      {isLoading ? <Skeleton width={200} /> : data.employee?.name || '--'}
    </Cell>
    <Cell minWidth={200} maxWidth={200} wrapText data-cy="transactionId">
      {isLoading ? <Skeleton width={200} /> : data.txnRefId || '--'}
    </Cell>
    <Cell minWidth={77} align="right" fontWeight="semiBold" data-cy="amount">
      {isLoading ? <Skeleton width={77} /> : centsToDollarsDisplay(data.amount)}
    </Cell>
  </Row>
);

const DetailCreditReportRow = (data: ILedgerEntry, index: number, dataCy: string, isLoading: boolean) => (
  <Row key={`${dataCy}-row-${index}`} data-cy={`${dataCy}-row`}>
    <Cell minWidth={77} data-cy="date">
      {isLoading ? <Skeleton width={77} /> : dayjs(data.createdAt).format('MM-DD-YYYY')}
    </Cell>
    <Cell minWidth={77} data-cy="type">
      {isLoading ? <Skeleton width={77} /> : startCase(data.entryType)}
    </Cell>
    <Cell minWidth={200} maxWidth={200} wrapText data-cy="description">
      {isLoading ? (
        <Skeleton width={200} />
      ) : (
        <>
          <span>{data.description || '--'}</span>
          {data.txnRefNotes && (
            <TooltipIcon id={`txn-ref-notes-${index}`} iconSize={13.25} iconMarginLeft={theme.space.xs} place="top">
              {data.txnRefNotes}
            </TooltipIcon>
          )}
        </>
      )}
    </Cell>
    <Cell minWidth={77} align="right" fontWeight="semiBold" data-cy="amount">
      {isLoading ? <Skeleton width={77} /> : centsToDollarsDisplay(data.amount)}
    </Cell>
  </Row>
);

const BankReconciliationTable: React.FC<IBankReconciliationTableProps> = ({ isLoading, tableData, reportType, dataCy = 'reports-table' }) => {
  const reportData = useMemo(() => {
    switch (reportType) {
      case BankReconciliationReportType.DETAILED_CREDITS:
        return {
          headers: DETAIL_CREDITS_REPORTS_HEADERS,
          rows: (tableData as ILedgerEntry[]).map((datum: ILedgerEntry, index: number) => DetailCreditReportRow(datum, index, dataCy, isLoading)),
        };
      case BankReconciliationReportType.DETAILED_DEBITS:
        return {
          headers: DETAIL_DEBITS_REPORTS_HEADERS,
          rows: (tableData as ILedgerEntry[]).map((datum: ILedgerEntry, index: number) => DetailDebitReportRow(datum, index, dataCy, isLoading)),
        };
      case BankReconciliationReportType.DAILY_CREDITS:
      case BankReconciliationReportType.DAILY_DEBITS:
      default:
        return {
          headers: DAILY_REPORTS_HEADERS,
          rows: (tableData as ILedgerEntryByDate[]).map((datum: ILedgerEntryByDate, index: number) => DailyReportRow(datum, index, dataCy, isLoading)),
        };
    }
  }, [reportType, tableData, dataCy, isLoading]);

  return (
    <Table data-cy={`${dataCy}-table`}>
      <TableHead>
        <HeaderRow>
          {reportData.headers.map((header: IBankReconciliationTableHeader, idx: number) => (
            <HeaderCell key={`${header.label}-${idx}`} align={header.align}>
              {isLoading ? '' : startCase(header.label)}
            </HeaderCell>
          ))}
        </HeaderRow>
      </TableHead>

      <TableBody>{reportData.rows}</TableBody>
    </Table>
  );
};

export default BankReconciliationTable;
